import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useController, useFormContext, useFormState } from 'react-hook-form'
import { type MultiSelectOption } from '../../../components/MultiSelectDropdown/MultiSelectDropdown'
import { IntegrationServiceId } from '../../../common/integrations'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { selectExternalId } from '../../../store/meetingSummary/contactUpdate'
import { useGetConnectorEntitiesQuery } from '../../../store/integrations/integrations'
import { useGetMeQuery } from '../../../store/user/user'
import { type ClicFpCodeMetadata } from '../models'

interface ClicFpCodesEntity {
  fpCodes?: string[]
  assignedFpCodes?: string[]
}

interface ClicFpCodeFormValues {
  metadata?: ClicFpCodeMetadata
}

type FpCodeMode = 'create' | 'update'
const NO_FP_CODES: string[] = []

const normalizeFpCode = (code: string) => code.trim().toUpperCase()

const buildFpCodeMetadata = (selected: string[], assignedCodes: string[]): ClicFpCodeMetadata => {
  const fpCodesToAdd = selected.filter((code) => !assignedCodes.includes(code))
  const fpCodesToRemove = assignedCodes.filter((code) => !selected.includes(code))
  return {
    fpCodes: [...assignedCodes.filter((code) => selected.includes(code)), ...fpCodesToAdd],
    fpCodesToAdd,
    fpCodesToRemove,
  }
}

export const useClicFpCodeField = (mode: FpCodeMode) => {
  const { setValue, getValues, resetField, control } = useFormContext<ClicFpCodeFormValues>()
  const { isSubmitted } = useFormState({ control })
  const accountId = useAppSelector(selectAccountId)
  const externalId = useAppSelector(selectExternalId)
  const { data: currentUser } = useGetMeQuery({ accountId })

  const ownFpCode = useMemo(() => {
    const ownExternalId = currentUser?.integrations?.find((i) => i.serviceId === IntegrationServiceId.CLIC)?.externalId
    return ownExternalId ? normalizeFpCode(ownExternalId) : undefined
  }, [currentUser])

  const relatedTo = mode === 'update' ? externalId : null
  const [mountedAt] = useState(() => Date.now())
  const { data, isFetching, fulfilledTimeStamp } = useGetConnectorEntitiesQuery(
    { accountId, connector: IntegrationServiceId.CLIC, entityType: 'fp_code', relatedTo },
    { skip: mode === 'update' && !externalId, refetchOnMountOrArgChange: true },
  )

  const hasCurrentData = !!data && (fulfilledTimeStamp ?? 0) >= mountedAt

  const entity = useMemo(() => {
    const first = hasCurrentData && Array.isArray(data?.data) ? data?.data[0] : undefined
    return (first ? Object.values(first)[0] : undefined) as ClicFpCodesEntity | undefined
  }, [data, hasCurrentData])

  const hierarchy = useMemo(
    () => Array.from(new Set([...(ownFpCode ? [ownFpCode] : []), ...(entity?.fpCodes ?? []).map(normalizeFpCode)])),
    [entity, ownFpCode],
  )
  const assignedCodes = useMemo(
    () => (mode === 'update' && entity?.assignedFpCodes ? Array.from(new Set(entity.assignedFpCodes.map(normalizeFpCode))) : NO_FP_CODES),
    [mode, entity],
  )
  const initialCodes = useMemo(() => (mode === 'update' ? assignedCodes : ownFpCode ? [ownFpCode] : NO_FP_CODES), [mode, assignedCodes, ownFpCode])

  const options = useMemo<MultiSelectOption[]>(
    () => [
      ...hierarchy.map((code) => ({ id: code, label: code })),
      ...assignedCodes.filter((code) => !hierarchy.includes(code)).map((code) => ({ id: code, label: code, displayLabel: `${code} (outside your hierarchy)` })),
    ],
    [hierarchy, assignedCodes],
  )

  const isReady = mode === 'create' ? !!currentUser : hasCurrentData && !isFetching
  const initializationKey = `${mode}:${relatedTo ?? ''}`
  const initializedFor = useRef<string | null>(null)

  useEffect(() => {
    if (!isReady || initializedFor.current === initializationKey) {
      return
    }
    initializedFor.current = initializationKey
    resetField('metadata', { defaultValue: { ...getValues('metadata'), ...buildFpCodeMetadata(initialCodes, assignedCodes) } })
  }, [isReady, initializationKey, initialCodes, assignedCodes, resetField, getValues])

  const validateFpCodes = (value?: string[]) => {
    const selected = value ?? []
    const changed = selected.length !== initialCodes.length || selected.some((code) => !initialCodes.includes(code))
    if (mode === 'update' && !changed) {
      return true
    }
    return selected.some((code) => hierarchy.includes(code)) || 'Keep at least one FP code from your hierarchy'
  }

  const { field, fieldState } = useController({ name: 'metadata.fpCodes', control, rules: { validate: validateFpCodes } })

  const setSelected = useCallback(
    (selected: string[]) => {
      setValue('metadata', { ...getValues('metadata'), ...buildFpCodeMetadata(selected, assignedCodes) }, { shouldValidate: isSubmitted, shouldDirty: true })
    },
    [setValue, getValues, assignedCodes, isSubmitted],
  )

  const selected = field.value ?? NO_FP_CODES
  const error = fieldState.error?.message

  return useMemo(() => ({ options, selected, setSelected, error, isFetching }), [options, selected, setSelected, error, isFetching])
}
